// Overlay primitives: drawers, modals, full-screen sheets, confirm/prompt dialogs, menus and
// popovers. One stack; Esc closes the top layer; Tab is trapped inside modal layers; focus returns
// to where it was; the app behind modal layers is made inert. See docs/UI-CONTRACT.md.
import { h, icon, uid, classNames, qsa, button, input, textarea, field } from '../dom.js';
import { t } from '../i18n/fr.js';

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])', 'select:not([disabled])',
  'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])', '[contenteditable="true"]',
].join(',');

/** Visible focusable elements inside `root`, in DOM order. */
export function focusableIn(root) {
  return qsa(FOCUSABLE, root).filter((el) => !el.closest('[hidden]') && el.getClientRects().length > 0);
}

function trapTab(e, panel) {
  const items = focusableIn(panel);
  if (!items.length) { e.preventDefault(); panel.focus(); return; }
  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;
  const outside = !panel.contains(active);
  if (e.shiftKey && (active === first || outside)) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && (active === last || outside)) {
    e.preventDefault();
    first.focus();
  }
}

/**
 * Creates the overlay manager.
 * @param {{host?: HTMLElement, appRoot?: HTMLElement|null, reducedMotion?: () => boolean}} [opts]
 *   host: where layers are appended (outside the app root); appRoot: made inert under modal layers.
 */
export function createOverlay({ host, appRoot = null, reducedMotion = () => false } = {}) {
  const layers = [];
  const hostEl = host || document.body;
  let listening = false;

  function syncInert() {
    const modal = layers.some((l) => l.modal);
    if (appRoot) appRoot.inert = modal;
    document.documentElement.classList.toggle('has-overlay', modal);
  }

  function onKeydown(e) {
    const top = layers[layers.length - 1];
    if (!top) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (top.dismissible) top.close('esc');
      return;
    }
    if (e.key === 'Tab') {
      if (top.modal) trapTab(e, top.panel);
      else if (top.closeOnTab) top.close('tab');
    }
  }

  function onPointerDown(e) {
    const top = layers[layers.length - 1];
    if (!top || top.modal) return;
    if (top.panel.contains(e.target) || top.anchor?.contains(e.target)) return;
    top.close('outside');
  }

  function onResize() {
    const top = layers[layers.length - 1];
    if (top && !top.modal) top.close('resize');
  }

  function listen(onOff) {
    if (onOff === listening) return;
    listening = onOff;
    const fn = onOff ? 'addEventListener' : 'removeEventListener';
    document[fn]('keydown', onKeydown, true);
    document[fn]('pointerdown', onPointerDown, true);
    window[fn]('resize', onResize);
  }

  /**
   * Registers a layer built by one of the open* functions.
   * @returns {Layer}
   */
  function register(kind, root, panel, parts, opts) {
    const prevFocus = document.activeElement;
    const closeFns = new Set();
    const layer = {
      id: parts.id, kind, el: root, panel, ...parts,
      modal: kind !== 'popover',
      dismissible: opts.dismissible !== false,
      anchor: opts.anchor || null,
      closeOnTab: !!opts.closeOnTab,
      closed: false,
      /** Closes the layer; reason: 'esc' | 'backdrop' | 'button' | 'outside' | 'route' | 'api' … */
      close(reason = 'api') {
        if (layer.closed) return;
        layer.closed = true;
        const idx = layers.indexOf(layer);
        if (idx >= 0) layers.splice(idx, 1);
        syncInert();
        if (!layers.length) listen(false);
        for (const fn of [...closeFns]) {
          try { fn(reason); } catch (err) { console.error(err); }
        }
        try { opts.onClose?.(reason); } catch (err) { console.error(err); }
        root.classList.add('is-closing');
        let removed = false;
        const remove = () => { if (!removed) { removed = true; root.remove(); } };
        if (reducedMotion() || kind === 'popover') remove();
        else { root.addEventListener('animationend', remove, { once: true }); setTimeout(remove, 280); }
        if (prevFocus && prevFocus.isConnected && typeof prevFocus.focus === 'function' && reason !== 'outside') {
          prevFocus.focus({ preventScroll: true });
        }
      },
      /** Adds a close callback; returns its remover. */
      onClose(fn) { closeFns.add(fn); return () => closeFns.delete(fn); },
      isOpen: () => !layer.closed,
    };
    layers.push(layer);
    root.style.setProperty('--layer-z', String(100 + layers.length * 2));
    hostEl.appendChild(root);
    syncInert();
    listen(true);
    // Initial focus: [autofocus], else first field (modals) or the panel itself (drawers, sheets).
    const target = opts.initialFocus instanceof Element ? opts.initialFocus
      : panel.querySelector('[autofocus]')
        || ((opts.initialFocus ?? (kind === 'modal' || kind === 'popover' ? 'first' : 'panel')) === 'first'
          ? focusableIn(parts.body || panel)[0] : null)
        || panel;
    requestAnimationFrame(() => { if (!layer.closed) target.focus({ preventScroll: true }); });
    return layer;
  }

  function header(parts, opts, closeLayer) {
    const titleId = `${parts.id}-title`;
    parts.titleEl = h('h2', { class: 'layer-title', id: titleId }, opts.title || '');
    parts.eyebrowEl = h('p', { class: 'layer-eyebrow mono-label', hidden: !opts.eyebrow }, opts.eyebrow || '');
    parts.subtitleEl = h('p', { class: 'layer-subtitle', hidden: !opts.subtitle }, opts.subtitle || '');
    parts.actionsEl = h('div', { class: 'layer-actions' }, opts.actions || null);
    parts.header = h('header', { class: 'layer-head' },
      h('div', { class: 'layer-titles' }, parts.eyebrowEl, parts.titleEl, parts.subtitleEl),
      parts.actionsEl,
      opts.dismissible === false ? null : h('button', {
        type: 'button', class: 'icon-btn layer-close', 'aria-label': t('overlay.close'), title: t('overlay.close'),
        onClick: () => closeLayer('button'),
      }, icon('close')));
    return titleId;
  }

  function withSetters(parts) {
    return Object.assign(parts, {
      setTitle(text) { parts.titleEl.textContent = text ?? ''; },
      setEyebrow(text) { parts.eyebrowEl.textContent = text ?? ''; parts.eyebrowEl.hidden = !text; },
      setSubtitle(text) { parts.subtitleEl.textContent = text ?? ''; parts.subtitleEl.hidden = !text; },
      setContent(...nodes) { parts.body.replaceChildren(); for (const n of nodes.flat()) if (n) parts.body.append(n); },
      setFooter(...nodes) {
        parts.footer.replaceChildren();
        for (const n of nodes.flat()) if (n) parts.footer.append(n);
        parts.footer.hidden = !parts.footer.childNodes.length;
      },
      setActions(...nodes) { parts.actionsEl.replaceChildren(); for (const n of nodes.flat()) if (n) parts.actionsEl.append(n); },
      setBusy(busy) {
        parts.progressEl.hidden = !busy;
        parts.body.setAttribute('aria-busy', busy ? 'true' : 'false');
      },
    });
  }

  function openPanel(kind, opts = {}) {
    const parts = { id: uid('layer') };
    let layer = null;
    const closeLayer = (reason) => layer?.close(reason);
    const titleId = header(parts, opts, closeLayer);
    parts.progressEl = h('div', { class: 'layer-progress', hidden: true, 'aria-hidden': 'true' });
    parts.body = h('div', { class: 'layer-body' }, opts.content || null);
    parts.footer = h('footer', { class: 'layer-foot', hidden: !opts.footer }, opts.footer || null);
    const size = opts.size || (kind === 'modal' ? 'sm' : 'md');
    const panel = h('section', {
      class: classNames([kind, `${kind}-${size}`, opts.tone && `tone-${opts.tone}`, opts.className]),
      role: opts.role || 'dialog', 'aria-modal': 'true', 'aria-labelledby': titleId, tabindex: '-1',
    }, parts.header, parts.progressEl, parts.body, parts.footer);
    const backdrop = h('div', { class: 'layer-backdrop', onClick: () => { if (opts.dismissible !== false) closeLayer('backdrop'); } });
    const root = h('div', { class: classNames(['layer', `layer-${kind}`]), dataset: { layer: parts.id } }, backdrop, panel);
    layer = register(kind, root, panel, withSetters(parts), opts);
    return layer;
  }

  /**
   * A drawer sliding from the right (full screen on mobile).
   * @param {{title?: string, eyebrow?: string, subtitle?: string, size?: 'md'|'lg'|'xl',
   *   content?: Node|Node[], footer?: Node|Node[], actions?: Node|Node[], onClose?: Function,
   *   dismissible?: boolean, className?: string, initialFocus?: 'first'|'panel'|Element}} opts
   * @returns {Layer}
   */
  function openDrawer(opts = {}) {
    return openPanel('drawer', opts);
  }

  /**
   * A centred modal dialog (bottom sheet on mobile). size: 'sm' | 'md' | 'lg'.
   * @returns {Layer}
   */
  function openModal(opts = {}) {
    return openPanel('modal', opts);
  }

  /**
   * A full-screen sheet (calendar, atelier…). tone 'dark' gives the black header of the calendar.
   * @returns {Layer}
   */
  function openSheet(opts = {}) {
    return openPanel('sheet', { size: 'full', ...opts });
  }

  /**
   * Confirmation dialog.
   * @param {{title?: string, message?: string, confirmLabel?: string, cancelLabel?: string, danger?: boolean}} opts
   * @returns {Promise<boolean>}
   */
  function confirm(opts = {}) {
    return new Promise((resolve) => {
      let answered = false;
      const done = (v) => { if (!answered) { answered = true; resolve(v); } };
      const ok = button(opts.confirmLabel || t('common.confirm'), {
        variant: opts.danger ? 'danger' : 'primary', onClick: () => { done(true); layer.close('confirm'); },
      });
      const cancel = button(opts.cancelLabel || t('common.cancel'), { variant: 'ghost', onClick: () => layer.close('cancel') });
      const layer = openModal({
        title: opts.title || t('overlay.confirmTitle'), size: 'sm', role: 'alertdialog',
        content: opts.message ? h('p', { class: 'layer-message' }, opts.message) : null,
        footer: [cancel, ok], initialFocus: opts.danger ? cancel : ok,
        onClose: () => done(false),
      });
    });
  }

  /**
   * Asks for a value.
   * @param {{title?: string, label?: string, value?: string, placeholder?: string, multiline?: boolean,
   *   confirmLabel?: string, required?: boolean, maxLength?: number, hint?: string}} opts
   * @returns {Promise<string|null>} the value, or null when cancelled
   */
  function prompt(opts = {}) {
    return new Promise((resolve) => {
      let answered = false;
      const done = (v) => { if (!answered) { answered = true; resolve(v); } };
      const props = { value: opts.value ?? '', placeholder: opts.placeholder, maxLength: opts.maxLength, autofocus: true };
      const control = opts.multiline ? textarea({ ...props, rows: 5 }) : input(props);
      const f = field(opts.label || '', control, { hint: opts.hint });
      const submit = (e) => {
        e?.preventDefault();
        const v = control.value;
        if (opts.required !== false && !v.trim()) { f.setError(t('common.required')); control.focus(); return; }
        done(v);
        layer.close('confirm');
      };
      const form = h('form', { class: 'stack', onSubmit: submit }, f);
      const layer = openModal({
        title: opts.title || t('overlay.promptTitle'), size: 'sm', content: form,
        footer: [button(t('common.cancel'), { variant: 'ghost', onClick: () => layer.close('cancel') }),
          button(opts.confirmLabel || t('common.ok'), { variant: 'primary', onClick: submit })],
        onClose: () => done(null),
      });
    });
  }

  function place(panel, anchor, placement = 'bottom-end') {
    const r = anchor.getBoundingClientRect();
    const pw = panel.offsetWidth;
    const ph = panel.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let top = placement.startsWith('top') ? r.top - ph - 8 : r.bottom + 8;
    if (top + ph > vh - 8 && r.top - ph - 8 > 8) top = r.top - ph - 8;
    if (top < 8) top = Math.min(r.bottom + 8, vh - ph - 8);
    let left = placement.endsWith('end') ? r.right - pw : placement.endsWith('center') ? r.left + r.width / 2 - pw / 2 : r.left;
    left = Math.max(8, Math.min(left, vw - pw - 8));
    panel.style.setProperty('top', `${Math.max(8, top)}px`);
    panel.style.setProperty('left', `${left}px`);
  }

  /**
   * A non-modal popover anchored to an element (closes on outside click, Esc, Tab, resize). Opening
   * it again from the same anchor while it is open closes it instead (a toggle) and returns the
   * closed layer (`isOpen()` false).
   * @param {HTMLElement} anchor
   * @param {Node|Node[]} content
   * @param {{placement?: 'bottom-start'|'bottom-end'|'bottom-center'|'top-start'|'top-end', className?: string,
   *   label?: string, role?: string, onClose?: Function}} [opts]
   * @returns {Layer}
   */
  function popover(anchor, content, opts = {}) {
    // A second click on the button of an open popover (floor menu, user menu…) closes it: the
    // pointerdown on the anchor does not (see onPointerDown), so the click would stack another.
    const open = layers.find((l) => l.kind === 'popover' && l.anchor === anchor && !l.closed);
    if (open) {
      open.close('toggle');
      return open;
    }
    const parts = { id: uid('pop') };
    parts.body = h('div', { class: 'popover-body' }, content);
    const panel = h('div', {
      class: classNames(['popover', opts.className]), role: opts.role || 'dialog', tabindex: '-1',
      'aria-label': opts.label || null,
    }, parts.body);
    const root = h('div', { class: 'layer layer-popover', dataset: { layer: parts.id } }, panel);
    const layer = register('popover', root, panel, parts, { ...opts, anchor, initialFocus: opts.initialFocus ?? 'first' });
    anchor.setAttribute?.('aria-expanded', 'true');
    layer.onClose(() => anchor.setAttribute?.('aria-expanded', 'false'));
    place(panel, anchor, opts.placement);
    return layer;
  }

  /**
   * A menu of actions anchored to an element.
   * items: [{label, icon?, onSelect, danger?, checked?, hint?, disabled?} | {separator: true} | {header: Node}]
   * @returns {Layer}
   */
  function menu(anchor, items, opts = {}) {
    let layer = null;
    const list = h('div', { class: 'menu', role: 'menu', 'aria-label': opts.label || null });
    for (const it of items) {
      if (!it) continue;
      if (it.separator) { list.appendChild(h('div', { class: 'menu-sep', role: 'separator' })); continue; }
      if (it.header) { list.appendChild(h('div', { class: 'menu-header' }, it.header)); continue; }
      list.appendChild(h('button', {
        type: 'button', class: classNames(['menu-item', { 'is-danger': it.danger }]),
        role: it.checked === undefined ? 'menuitem' : 'menuitemcheckbox',
        'aria-checked': it.checked === undefined ? null : String(!!it.checked), disabled: !!it.disabled,
        onClick: () => { layer.close('select'); it.onSelect?.(); },
      }, it.icon ? icon(it.icon, { size: 16 }) : h('span', { class: 'menu-icon-spacer' }),
      h('span', { class: 'menu-label' }, it.label),
      it.hint ? h('span', { class: 'menu-hint' }, it.hint) : null));
    }
    list.addEventListener('keydown', (e) => {
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
      e.preventDefault();
      const btns = focusableIn(list);
      const idx = btns.indexOf(document.activeElement);
      const next = e.key === 'Home' ? 0 : e.key === 'End' ? btns.length - 1
        : (idx + (e.key === 'ArrowDown' ? 1 : -1) + btns.length) % btns.length;
      btns[next]?.focus();
    });
    layer = popover(anchor, list, { ...opts, closeOnTab: true, role: 'presentation', className: classNames(['popover-menu', opts.className]) });
    return layer;
  }

  return {
    openDrawer, openModal, openSheet, confirm, prompt, popover, menu,
    /** Closes the top-most layer; returns true when one was closed. */
    closeTop(reason = 'api') {
      const top = layers[layers.length - 1];
      if (!top) return false;
      top.close(reason);
      return true;
    },
    /** Closes every layer. */
    closeAll(reason = 'api') {
      for (const l of [...layers].reverse()) l.close(reason);
    },
    /** Number of open layers (modal or not). */
    count: () => layers.length,
    /** Number of open modal layers. */
    modalCount: () => layers.filter((l) => l.modal).length,
    /** The top-most layer or null. */
    top: () => layers[layers.length - 1] || null,
  };
}

/**
 * @typedef {object} Layer
 * @property {string} id
 * @property {'drawer'|'modal'|'sheet'|'popover'} kind
 * @property {HTMLElement} el      root element of the layer
 * @property {HTMLElement} panel   the dialog element
 * @property {HTMLElement} body    scrollable content area
 * @property {HTMLElement} [header]
 * @property {HTMLElement} [footer]
 * @property {(text: string) => void} [setTitle]
 * @property {(text: string) => void} [setEyebrow]
 * @property {(text: string) => void} [setSubtitle]
 * @property {(...nodes: Node[]) => void} [setContent]
 * @property {(...nodes: Node[]) => void} [setFooter]
 * @property {(...nodes: Node[]) => void} [setActions]
 * @property {(busy: boolean) => void} [setBusy]
 * @property {(reason?: string) => void} close
 * @property {(fn: (reason: string) => void) => () => void} onClose
 * @property {() => boolean} isOpen
 */
